import React from 'react';
import { CheckCircle2, MessageSquare, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import styles from './Pricing.module.css';

const Pricing = () => {
  const brandOrange = "#e87355";
  const brandBlue = "#5ba5e0";
  const { t } = useLanguage();

  const standardPlans = [
    {
      name: t('plan_starter_name'),
      price: t('plan_starter_price'),
      duration: t('plan_starter_duration'),
      tagline: t('plan_starter_tagline'),
      features: [
        t('plan_starter_f1'),
        t('plan_starter_f2'),
        t('plan_starter_f3'),
        t('plan_starter_f4')
      ],
      premium: false,
      buttonText: t('nav_demo_btn'),
    },
    {
      name: t('plan_growth_name'),
      price: t('plan_growth_price'),
      duration: t('plan_growth_duration'),
      tagline: t('plan_growth_tagline'),
      features: [
        t('plan_growth_f1'),
        t('plan_growth_f2'),
        t('plan_growth_f3'),
        t('plan_growth_f4')
      ],
      premium: true,
      badge: "POPULAR",
      buttonText: t('nav_demo_btn'),
    }
  ];

  const realFacePlans = [
    {
      name: t('plan_personal_name'),
      price: t('plan_personal_price'),
      duration: t('plan_personal_duration'),
      tagline: t('plan_personal_tagline'),
      features: [
        t('plan_personal_f1'),
        t('plan_personal_f2'),
        t('plan_personal_f3'),
        t('plan_personal_f4')
      ],
      premium: false,
      buttonText: t('nav_demo_btn'),
    },
    {
      name: t('plan_premium_personal_name'),
      price: t('plan_premium_personal_price'),
      duration: t('plan_premium_personal_duration'),
      tagline: t('plan_premium_personal_tagline'),
      features: [
        t('plan_premium_personal_f1'),
        t('plan_premium_personal_f2'),
        t('plan_premium_personal_f3'),
        t('plan_premium_personal_f4')
      ],
      premium: true,
      badge: "BEST VALUE",
      buttonText: t('nav_demo_btn'),
    }
  ];

  const handleCtaClick = () => {
    // Scroll to contact WhatsApp or external CTA
    const phoneNumber = "917533928185";
    const waMessage = encodeURIComponent("Hello Ananta Team, I'm interested in starting a plan for AI News Anchors.");
    window.open(`https://wa.me/${phoneNumber}?text=${waMessage}`, '_blank');
  };

  return (
    <section id="pricing" className={styles.pricing}>
      <h2 className={styles.sectionTitle}>
        {t('pricing_title')} <span className={styles.neonText}>{t('pricing_title_accent')}</span>
      </h2>

      {/* --- STANDARD AI ANCHOR PLANS --- */}
      <div className={styles.categoryContainer}>
        <div className={styles.categoryHeader}>
          <h3>{t('plan_standard_title')}</h3>
          <p>{t('plan_standard_desc')}</p>
        </div>
        
        <div className={styles.pricingGrid}>
          {standardPlans.map((plan, index) => (
            <div 
              key={index} 
              className={`${styles.glassCard} ${styles.priceCard} ${plan.premium ? styles.premiumCard : ''}`}
            >
              {plan.badge && <div className={styles.popularBadge}>{plan.badge}</div>}
              
              <h4 className={styles.planName}>{plan.name}</h4>
              <div className={styles.planPrice}>{plan.price} <span>{plan.duration}</span></div>
              <p className={styles.planTagline}>{plan.tagline}</p>
              
              <ul className={styles.planFeatures}>
                {plan.features.map((feature, fIdx) => (
                  <li key={fIdx}>
                    <CheckCircle2 size={18} color={brandBlue} /> {feature}
                  </li>
                ))}
              </ul>

              <button 
                className={plan.premium ? styles.btnPrimary : styles.btnSecondary}
                onClick={handleCtaClick}
              >
                {plan.buttonText}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* --- REAL FACE AI ANCHOR PLANS --- */}
      <div className={styles.categoryContainer} style={{ marginTop: '80px' }}>
        <div className={styles.categoryHeader}>
          <h3>{t('plan_realface_title')}</h3>
          <p>{t('plan_realface_desc')}</p>
        </div>
        
        <div className={styles.pricingGrid}>
          {realFacePlans.map((plan, index) => (
            <div 
              key={index} 
              className={`${styles.glassCard} ${styles.priceCard} ${plan.premium ? styles.premiumCard : ''}`}
            >
              {plan.badge && <div className={styles.popularBadge}>{plan.badge}</div>}
              
              <h4 className={styles.planName}>{plan.name}</h4>
              <div className={styles.planPrice}>{plan.price} <span>{plan.duration}</span></div>
              <p className={styles.planTagline}>{plan.tagline}</p>
              
              <ul className={styles.planFeatures}>
                {plan.features.map((feature, fIdx) => (
                  <li key={fIdx}>
                    <CheckCircle2 size={18} color={brandOrange} /> {feature}
                  </li>
                ))}
              </ul>

              <button 
                className={plan.premium ? styles.btnPrimary : styles.btnSecondary}
                onClick={handleCtaClick}
              >
                {plan.buttonText}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* --- CUSTOM / ENTERPRISE BLOCK --- */}
      <div className={styles.customBanner}>
        <div className={styles.customLeft}>
          <Zap size={36} color={brandOrange} className={styles.zapIcon} />
          <div>
            <h3>{t('plan_custom_name')}</h3>
            <p>{t('plan_custom_desc')}</p>
          </div>
        </div>
        <button className={styles.customBtn} onClick={handleCtaClick}>
          <MessageSquare size={18} /> {t('plan_custom_btn')}
        </button>
      </div>
    </section>
  );
};

export default Pricing;